import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import Select from 'react-select';
import Form from '../form/Form';
import FieldGroup from '../field_group/FieldGroup';
import { requiredValidator } from '../field_group/validators';
import { getErrorValue } from '../../utils/utility';
import { listVRFIndoor } from '../../redux/vrf/vrf_indoor_actions';
import { listVRFOutdoor } from '../../redux/vrf/vrf_outdoor_actions';

// FRS 4.2.1 (revision 20260826) - VRF System form
// 3 แท็บตาม mockup หน้า 6-8: Basic Info / Outdoor Unit / Indoor Units
// ใช้ nav-tabs toggle เอง (ไม่ใช้ react-bootstrap <Tabs>) — bug เดิม: Tabs ไม่ render
// children ตรงๆ ทำให้ Form.deepMap หา FieldGroup ไม่เจอ + remount → infinite setState loop
export class VRFSystemDetail extends React.Component {
  constructor(props) {
    super(props);
    this.formRef = undefined;
    const sys = props.system || {};
    this.state = {
      activeTab: 'basic',
      outdoorId: sys.outdoor ? sys.outdoor.id : sys.outdoor_id || null,
      // indoorItems: [{ indoorTypeId, quantity }]
      // sys.indoors comes flattened from VRFSystemSerializer.get_indoors() —
      // each entry IS the indoor type's own serialized fields (id/name/...)
      // merged with quantity, not a nested indoor_type object.
      indoorItems: (sys.indoors || []).map(it => ({
        indoorTypeId: it.id,
        quantity: it.quantity || 1,
      })),
      addIndoorId: null,
      addIndoorQty: 1,
      addOutdoorId: null,
      // UAT feedback 9/10/2569: แก้ outdoor/indoor แล้ว Save ไม่ติด เพราะ Form นับ dirty
      // เฉพาะ FieldGroup (ชื่อ/คำอธิบาย) — ติดตาม dirty ของ outdoor/indoor แยกเอง
      itemsDirty: false,
      outdoorError: null,
    };
  }

  componentDidMount() {
    this.props.listIndoor();
    this.props.listOutdoor();
  }

  componentWillReceiveProps(nextProps) {
    // ไม่ทับสิ่งที่ผู้ใช้แก้ค้างไว้ ถ้า system object ถูก refresh ระหว่างแก้ (เช่น GET ตอบกลับช้า)
    if (
      nextProps.system !== this.props.system &&
      nextProps.system &&
      !this.state.itemsDirty
    ) {
      const sys = nextProps.system;
      this.setState({
        outdoorId: sys.outdoor ? sys.outdoor.id : sys.outdoor_id || null,
        indoorItems: (sys.indoors || []).map(it => ({
          indoorTypeId: it.id,
          quantity: it.quantity || 1,
        })),
      });
    }
    // error จาก backend อยู่ในแท็บที่ซ่อนอยู่ → สลับไปแท็บนั้นให้เห็น (เดิมกด Save แล้วเงียบ)
    if (
      nextProps.fieldErrors &&
      nextProps.fieldErrors !== this.props.fieldErrors
    ) {
      const tab = this.tabOfErrors(nextProps.fieldErrors);
      if (tab) this.setState({ activeTab: tab });
    }
  }

  tabOfErrors(fieldErrors) {
    if (!fieldErrors) return null;
    if (fieldErrors.name || fieldErrors.description) return 'basic';
    if (fieldErrors.outdoor_id) return 'outdoor';
    if (fieldErrors.indoor_items) return 'indoor';
    return null;
  }

  markItemsDirty(patch) {
    this.setState({ ...patch, itemsDirty: true }, () => {
      if (this.props.onItemsDirty) this.props.onItemsDirty(true);
    });
  }

  // เรียกจาก modal ก่อน save — 1 ระบบต้องมี outdoor 1 ตัวเสมอ (FK not null ฝั่ง backend)
  validate() {
    const { t } = this.props;
    if (!this.state.outdoorId) {
      this.setState({
        activeTab: 'outdoor',
        outdoorError: t('please select an outdoor unit'),
      });
      return false;
    }
    return true;
  }

  getData() {
    return {
      ...this.formRef.getFormData([]),
      outdoorId: this.state.outdoorId,
      indoorItems: this.state.indoorItems,
    };
  }

  addIndoor() {
    const { addIndoorId, addIndoorQty, indoorItems } = this.state;
    if (!addIndoorId) return;
    if (indoorItems.some(it => it.indoorTypeId === addIndoorId)) return;
    this.markItemsDirty({
      indoorItems: [
        ...indoorItems,
        { indoorTypeId: addIndoorId, quantity: Number(addIndoorQty) || 1 },
      ],
      addIndoorId: null,
      addIndoorQty: 1,
    });
  }

  removeIndoor(indoorTypeId) {
    this.markItemsDirty({
      indoorItems: this.state.indoorItems.filter(
        it => it.indoorTypeId !== indoorTypeId,
      ),
    });
  }

  updateIndoorQty(indoorTypeId, quantity) {
    this.markItemsDirty({
      indoorItems: this.state.indoorItems.map(it =>
        it.indoorTypeId === indoorTypeId ? { ...it, quantity } : it,
      ),
    });
  }

  addOutdoor() {
    const { addOutdoorId } = this.state;
    if (!addOutdoorId) return;
    this.markItemsDirty({
      outdoorId: addOutdoorId,
      addOutdoorId: null,
      outdoorError: null,
    });
  }

  removeOutdoor() {
    this.markItemsDirty({ outdoorId: null });
  }

  renderTabNav() {
    const { t, fieldErrors } = this.props;
    const { activeTab, outdoorError } = this.state;
    const errorTabs = {
      basic: !!(fieldErrors && (fieldErrors.name || fieldErrors.description)),
      outdoor: !!(outdoorError || (fieldErrors && fieldErrors.outdoor_id)),
      indoor: !!(fieldErrors && fieldErrors.indoor_items),
    };
    const tab = (key, label) => (
      <li className={activeTab === key ? 'active' : ''}>
        <a
          href="#"
          style={errorTabs[key] ? { color: '#a94442' } : undefined}
          onClick={e => {
            e.preventDefault();
            this.setState({ activeTab: key });
          }}
        >
          {label}
          {errorTabs[key] && (
            <i
              className="fas fa-exclamation-circle"
              style={{ marginLeft: 6 }}
            />
          )}
        </a>
      </li>
    );
    return (
      <ul className="nav nav-tabs" style={{ marginBottom: 15 }}>
        {tab('basic', t('basic info'))}
        {tab('outdoor', t('outdoor unit'))}
        {tab('indoor', t('indoor units'))}
      </ul>
    );
  }

  renderIndoorTab() {
    const { t, indoorOptions } = this.props;
    const { indoorItems, addIndoorId, addIndoorQty } = this.state;
    const byId = id => indoorOptions.find(o => o.id === id) || {};
    const options = indoorOptions
      .filter(o => !indoorItems.some(it => it.indoorTypeId === o.id))
      .map(o => ({ value: o.id, label: o.name }));

    return (
      <div style={{ padding: 10 }}>
        <p style={{ color: '#888' }}>
          {t('select indoor unit from library (multi-select + quantity)')}
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>{t('indoor unit')}</th>
              <th style={{ width: 120 }}>{t('indoor form')}</th>
              <th style={{ width: 140 }}>{t('cooling capacity')}</th>
              <th style={{ width: 90 }}>{t('qty')}</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {indoorItems.length === 0 && (
              <tr>
                <td colSpan={5} style={{ color: '#aaa' }}>
                  {t('no indoor units')}
                </td>
              </tr>
            )}
            {indoorItems.map(it => {
              const ind = byId(it.indoorTypeId);
              return (
                <tr key={it.indoorTypeId}>
                  <td>{ind.name || `#${it.indoorTypeId}`}</td>
                  <td>{ind.indoor_form || '-'}</td>
                  <td>
                    {ind.cooling_capacity != null
                      ? `${ind.cooling_capacity} ${ind.unit || ''}`
                      : '-'}
                  </td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      className="form-control"
                      value={it.quantity}
                      disabled={this.props.disabled}
                      onChange={e =>
                        this.updateIndoorQty(it.indoorTypeId, e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link"
                      disabled={this.props.disabled}
                      onClick={() => this.removeIndoor(it.indoorTypeId)}
                    >
                      {t('remove')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!this.props.disabled && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, marginRight: 10 }}>
              <Select
                classNamePrefix="react-select"
                placeholder={t('select indoor unit from library')}
                value={options.filter(o => o.value === addIndoorId)}
                options={options}
                onChange={opt =>
                  this.setState({ addIndoorId: opt ? opt.value : null })
                }
              />
            </div>
            <input
              type="number"
              min={1}
              style={{ width: 80, marginRight: 10 }}
              className="form-control"
              value={addIndoorQty}
              onChange={e => this.setState({ addIndoorQty: e.target.value })}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => this.addIndoor()}
            >
              {t('add indoor')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // UAT feedback 9/10/2569 (หน้า 3): เปลี่ยนจาก dropdown ตรงๆ เป็นแบบเดียวกับ Indoor —
  // เลือกจากคลัง + ปุ่ม "Add Outdoor" แล้วแสดงเป็นแถวในตาราง (มีได้ตัวเดียว ต้อง Remove ก่อนเปลี่ยน)
  renderOutdoorTab() {
    const { t, disabled, fieldErrors, outdoorOptions } = this.props;
    const { outdoorId, addOutdoorId, outdoorError } = this.state;
    const selected = outdoorId
      ? outdoorOptions.find(o => o.id === outdoorId) || { id: outdoorId }
      : null;
    const options = outdoorOptions.map(o => ({ value: o.id, label: o.name }));
    const fmt = v => (v === null || v === undefined || v === '' ? '-' : v);
    const errorText =
      outdoorError || getErrorValue(fieldErrors, 'outdoor_id') || null;

    return (
      <div>
        <table className="table">
          <thead>
            <tr>
              <th>{t('outdoor unit')}</th>
              <th style={{ width: 140 }}>{t('cooling capacity')}</th>
              <th style={{ width: 110 }}>{t('power (kW)')}</th>
              <th style={{ width: 80 }}>SEER</th>
              <th style={{ width: 80 }}>COP</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {!selected && (
              <tr>
                <td colSpan={6} style={{ color: '#aaa' }}>
                  {t('no outdoor unit')}
                </td>
              </tr>
            )}
            {selected && (
              <tr>
                <td>{selected.name || `#${selected.id}`}</td>
                <td>
                  {selected.cooling_capacity != null
                    ? `${selected.cooling_capacity} ${selected.unit || ''}`
                    : '-'}
                </td>
                <td>{fmt(selected.power)}</td>
                <td>{fmt(selected.seer)}</td>
                <td>
                  {selected.cop == null ? '-' : Number(selected.cop).toFixed(3)}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link"
                    disabled={disabled}
                    onClick={() => this.removeOutdoor()}
                  >
                    {t('remove')}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!disabled && !selected && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, marginRight: 10 }}>
              <Select
                classNamePrefix="react-select"
                placeholder={t('select outdoor unit from library')}
                value={options.filter(o => o.value === addOutdoorId)}
                options={options}
                onChange={opt =>
                  this.setState({ addOutdoorId: opt ? opt.value : null })
                }
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!addOutdoorId}
              onClick={() => this.addOutdoor()}
            >
              {t('add outdoor')}
            </button>
          </div>
        )}
        {errorText && (
          <div className="has-error" style={{ marginTop: 8 }}>
            <span className="help-block">{errorText}</span>
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      system,
      t,
      disabled,
      fieldErrors,
      outdoorOptions,
      indoorOptions,
      onItemsDirty,
      listIndoor,
      listOutdoor,
      ...otherAttr
    } = this.props;
    const { name, description } = system || {};
    const { activeTab } = this.state;
    const customAttr = { disabled };

    return (
      <div>
        {this.renderTabNav()}

        <div style={{ display: activeTab === 'basic' ? 'block' : 'none' }}>
          <Form
            {...otherAttr}
            ref={ref => (this.formRef = ref)}
            errorMessage={getErrorValue(fieldErrors, 'detail')}
          >
            <FieldGroup
              id="vrf-system-name"
              field="name"
              type="text"
              label={t('system name')}
              placeholder="VRF-1"
              value={name}
              validators={[requiredValidator]}
              errorMessage={getErrorValue(fieldErrors, 'name')}
              {...customAttr}
            />
            <FieldGroup
              id="vrf-system-description"
              field="description"
              componentClass="textarea"
              label={t('description')}
              value={description}
              {...customAttr}
            />
          </Form>
        </div>

        <div
          style={{
            display: activeTab === 'outdoor' ? 'block' : 'none',
            padding: 10,
          }}
        >
          <p style={{ color: '#888' }}>
            {t('one vrf system has exactly one outdoor unit')}
          </p>
          {this.renderOutdoorTab()}
        </div>

        <div style={{ display: activeTab === 'indoor' ? 'block' : 'none' }}>
          {this.renderIndoorTab()}
        </div>
      </div>
    );
  }
}

VRFSystemDetail.defaultProps = {
  system: {},
  indoorOptions: [],
  outdoorOptions: [],
};

const mapStateToProps = state => ({
  indoorOptions: state.vrfIndoor.orderedIds.map(
    id => state.vrfIndoor.objects[id],
  ),
  outdoorOptions: state.vrfOutdoor.orderedIds.map(
    id => state.vrfOutdoor.objects[id],
  ),
});

const mapDispatchToProps = dispatch => ({
  listIndoor: () => dispatch(listVRFIndoor(200, 0, null, null, null)),
  listOutdoor: () => dispatch(listVRFOutdoor(200, 0, null, null, null)),
});

export default translate('translations', { withRef: true })(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { withRef: true },
  )(VRFSystemDetail),
);



// WEBPACK FOOTER //
// ./src/components/vrf/VRFSystemDetail.js